import React from "react";

import { Colors } from "@daytrip/horizon-util-ts-tokens";
import styled, { css } from "styled-components";

import { Dimensions } from "../Dimensions";

export const DropdownOptionPlaceholder = styled.span`
    color: ${Colors.Grey200};
`;

export const DropdownOptionWrapper = styled.div<{ isFocused: boolean }>`
    font-size: ${Dimensions.d14};
    line-height: ${Dimensions.d20};
    font-weight: 400;
    padding: ${Dimensions.d8} ${Dimensions.d12};

    &:hover {
        background-color: ${Colors.Blue100};
        color: ${Colors.Blue600};
    }

    ${({ isFocused }) =>
        isFocused &&
        css`
            background-color: ${Colors.Blue100};
            color: ${Colors.Blue500};
        `}
`;

type SelectOption = {
    label: string;
    value: string | number;
    disabled?: boolean;
    [property: string]: any;
};

interface IDropdownOption {
    content: SelectOption;
    isFocused: boolean;
    onSelect: () => void;
    optionRenderer?: (option: SelectOption) => React.ReactNode;
}

interface IDropdownOptionRenderer {
    optionRenderer?: (option: SelectOption) => React.ReactNode;
    content?: SelectOption;
    placeholder?: string;
}

export const DropdownOptionRenderer: React.FC<React.PropsWithChildren<IDropdownOptionRenderer>> = (props) => {
    const { optionRenderer, content, placeholder } = props;

    if (!(content || placeholder)) {
        return null;
    }

    const placeholderID = "__placeholder";

    const defaultOptionRender = (option: SelectOption) => {
        if (option.value === placeholderID) {
            return <DropdownOptionPlaceholder>{option.label}</DropdownOptionPlaceholder>;
        }
        return option.label;
    };

    const definedOptionRender = optionRenderer || defaultOptionRender;
    const displayContent = content || ({ label: placeholder, value: placeholderID } as SelectOption);

    return definedOptionRender(displayContent) as React.ReactElement;
};

export const DropdownOption: React.FC<React.PropsWithChildren<IDropdownOption>> = (props) => {
    const { isFocused, onSelect, ...contentRenderer } = props;

    return (
        <DropdownOptionWrapper
            isFocused={isFocused}
            onClick={onSelect}
            onMouseDown={(event) => {
                // This is intended, do not remove. Fixes Safari onClick/onBlur dispatch order.
                event.preventDefault();
            }}
        >
            <DropdownOptionRenderer content={contentRenderer.content} optionRenderer={contentRenderer.optionRenderer}>
                {contentRenderer.children}
            </DropdownOptionRenderer>
        </DropdownOptionWrapper>
    );
};
